import React, { useEffect, useState } from 'react';

import { t } from 'i18next';
import { City } from 'country-state-city';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import Yup from '../../../../utils/yupExtensions';
import styles from './EmployerPortalApplications.module.css';
import employersJobStyles from '../EmployerPortalJobs/EmployerPortalJobs.module.css';
import GlobalModal from '../../Popups/GlobalModal';
import ApplicationsList from './ApplicationsList';
import ApplicationsDetail from './ApplicationsDetail';
import { filterUI } from '../EmployerPortalCandidates/FilterUI';
import { Typography } from '../../../Typography/typography';
import { getJobSeekerList } from '../../../../services/TalentsService';
import { getEmployerApplicationsList, getJobsList } from '../../../../services/jobService';

function EmployerPortalApplication() {
    const [page, setPage] = useState(1); // Current page number
    const [jobs, setJobs] = useState([]);
    const [cities, setCities] = useState([]);
    const [perPage, setPerPage] = useState(10); // Items per page
    const [totalPages, setTotalPages] = useState(1); // Total pages
    const [candidates, setCandidates] = useState([]);
    const [listOptions, setListOptions] = useState([]);
    const [activeButton, setActiveButton] = useState(t('candidatesFilterUI.all'));
    const [totalCandidates, setTotalCandidates] = useState(0); // Total candidates
    const [filteredCandidates, setFilteredCandidates] = useState([]);
    const [applicationDetail, setApplicationDetail] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [sortBy, setSortBy] = useState('newest');
    const [showSortDropdown, setShowSortDropdown] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [limitedApplications, setLimitedApplications] = useState([]);
    const [hasMoreApplications, setHasMoreApplications] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const user = useSelector(state => state.user.user);
    const userToken = useSelector(state => state.user.token);
    const features = useSelector(state => state.user.features);
    const applicationsLimit = features?.applicationsPerJob;

    const navigate = useNavigate()

    const formik = useFormik({
        initialValues: {
            title: '',
            city: '',
            country: '',
            list: '',
        },
        validationSchema: Yup.object({}),
        onSubmit: async (values) => { },
    });
    const location = useLocation();

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const cities = City.getCitiesOfCountry(formik.values.country);
        const citiesFormat = cities.map(city => ({
            value: city.name,
            label: city.name
        }));
        setCities(citiesFormat);
    }, [formik.values.country]);

    useEffect(() => {
        getJobsList(page, 1000, null, { employer_id: user.id }, user.jobseeker_status || user.employer_status)
            .then((response) => {
                const jobsFormat = response.jobs.map(job => {
                    return ({
                        value: job.id,
                        label: job.title
                    })
                });
                setJobs(jobsFormat);
            })
            .catch((error) => {
                console.error('Failed to fetch jobs after retries:', error);
            });
    }, [user]);

    useEffect(() => {
        const getCandidateList = async () => {
            setIsLoading(true);
            try {
                const employer_id = user?.id;
                const token = userToken;
                const filters = {
                    show_new: false,
                    show_shortlisted: false,
                    show_reviewing: false,
                    show_interviewing: false,
                    show_hired: false,
                    show_rejected: false,
                    show_undecided: false,
                };
                if (activeButton == t("candidatesFilterUI.new")) filters.show_new = true
                if (activeButton == t("candidatesFilterUI.shortlisted")) filters.show_shortlisted = true
                if (activeButton == t("candidatesFilterUI.undecided")) filters.show_undecided = true
                if (activeButton == t("candidatesFilterUI.rejected")) filters.show_undecided = true
                const response = await getEmployerApplicationsList(employer_id, token, page, perPage, filters);

                // Sort applications when they are first fetched
                if (response?.applications?.length > 0 && sortBy === 'newest') {
                    response.applications.sort((a, b) => {
                        const dateA = new Date(a?.application?.createdAt || 0);
                        const dateB = new Date(b?.application?.createdAt || 0);
                        return dateB - dateA; // Newest first
                    });
                }

                setCandidates(response);
                setFilteredCandidates(response);
                if (response && response?.applications?.length > 0) setApplicationDetail(response?.applications?.filter((e) => e?.jobseeker)[0])
                else setApplicationDetail(null)
                setTotalPages(response.totalPages);
                setTotalCandidates(response.totalCandidates);
            } catch (error) {
                console.error('Error fetching applications:', error);
            } finally {
                setIsLoading(false);
            }
        };
        getCandidateList();
    }, [user, userToken, activeButton, page, perPage, sortBy]);

    useEffect(() => {
        const fetchFavoriteLists = async () => {
            try {
                const response = await getJobSeekerList(user.id)
                const fetchedLists = response.data.favoriteLists.map((item) => ({
                    id: item.id,
                    label: item.list_name,
                    value: item.list_name,
                }));
                setListOptions(fetchedLists)
            } catch (error) {
                console.error("Error fetching favorite lists:", error);
            }
        };
        fetchFavoriteLists()
    }, [user.id]);

    const handleFindCandidates = () => {
    }

    useEffect(() => {
        if (!candidates?.applications) return;

        let filtered = [...candidates.applications];

        if (formik.values.city) {
            filtered = filtered.filter(e =>
                e?.jobseeker?.city?.toLowerCase() === formik.values.city?.toLowerCase()
            );
        }

        if (formik.values.country) {
            filtered = filtered.filter(e =>
                e?.jobseeker?.country?.toLowerCase() === formik.values.country?.toLowerCase()
            );
        }

        if (formik.values.title) {

            // Check if title is an array of selected job IDs
            if (Array.isArray(formik.values.title) && formik.values.title.length > 0) {
                // Extract the job IDs from the selected options
                const selectedJobIds = formik.values.title.map(item => item);
                // Filter applications where the job ID matches any of the selected IDs
                filtered = filtered?.filter(e => selectedJobIds.includes(e?.jobDetail?.id));
            }
            else if (typeof formik.values.title === 'string') {
                // Fall back to the original text-based filtering if not using multi-select
                filtered = filtered?.filter((e) => e?.jobDetail?.title?.toLowerCase()?.includes(formik.values.title.toLowerCase()));
            }
        }

        // Sort applications based on sortBy value
        if (sortBy === 'newest') {
            filtered.sort((a, b) => {
                const dateA = new Date(a?.application?.createdAt || 0);
                const dateB = new Date(b?.application?.createdAt || 0);
                return dateB - dateA; // Newest first
            });
        } else if (sortBy === 'oldest') {
            filtered.sort((a, b) => {
                const dateA = new Date(a?.application?.createdAt || 0);
                const dateB = new Date(b?.application?.createdAt || 0);
                return dateA - dateB; // Oldest first
            });
        }

        // Apply limit to applications based on subscription plan
        if (filtered.length > applicationsLimit) {
            setHasMoreApplications(true);
            setLimitedApplications({
                ...candidates,
                applications: filtered.slice(0, applicationsLimit)
            });
        } else {
            setHasMoreApplications(false);
            setLimitedApplications({
                ...candidates,
                applications: filtered
            });
        }

        setFilteredCandidates({
            ...candidates,
            applications: filtered
        });

        // Set the first candidate as selected if available
        if (filtered.length > 0) {
            setApplicationDetail(filtered.find(e => e?.jobseeker));
        } else {
            setApplicationDetail(null);
        }
    }, [formik.values.city, formik.values.country, formik.values.title, candidates, sortBy, applicationsLimit,location?.state?.job_id]);

    useEffect(() => {
        if (formik.values.country) {
            formik.setFieldValue('city', ''); // Clear city field
        }
    }, [formik.values.country]);

    const handleGetCount = (index) => {
        if (filteredCandidates?.statusCounts) {
            const { Hired, Interviewing, New, Rejected, Reviewing, Shortlisted, Undecided } = filteredCandidates?.statusCounts
            if (index == 0) return Hired + Interviewing + New + Rejected + Reviewing + Shortlisted + Undecided
            else if (index == 1) return New
            else if (index == 2) return Shortlisted
            else if (index == 3) return Undecided
            else if (index == 4) return Rejected
        }
    }

    useEffect(() => {
        const filter = [
            t("candidatesFilterUI.all"),
            t("candidatesFilterUI.new"),
            t("candidatesFilterUI.shortlisted"),
            t("candidatesFilterUI.undecided"),
            t("candidatesFilterUI.rejected"),
        ];
        const data = filter.filter((item) => item == location?.state?.title)
        if (data?.length > 0) {
            setActiveButton(data[0])
        }
        if (location?.state?.job_id) formik.setFieldValue('title', location?.state?.job_id);
    }, [location?.state?.title, location?.state?.job_id])

    const handleViewMoreClick = () => setShowUpgradeModal(true);

    return (
        <div className={styles.container}>
            {filterUI(
                formik,
                activeButton,
                setActiveButton,
                handleGetCount,
                jobs,
                cities,
                listOptions,
                handleFindCandidates,
                true,
                false,
                null,
                sortBy,
                setSortBy,
                showSortDropdown,
                setShowSortDropdown,
                true
            )}
            <div className={`${styles.row} ${styles.applicantsBody}`}>
                {isLoading ? (
                    <div className={employersJobStyles.loaderContainer}>
                        <div className={employersJobStyles.loader}></div>
                        <p>{t('forms.login.processing')}</p>
                    </div>
                ) : (
                    <>
                        {(!isMobile || !applicationDetail) && (
                            <div className={styles.applicationListContainer} >
                                <ApplicationsList
                                    selectedApplication={applicationDetail}
                                    setApplicationDetail={setApplicationDetail}
                                    applications={limitedApplications}
                                />
                                {hasMoreApplications && (
                                    <div className={styles.viewMoreContainer}>
                                        <button className={styles.viewMoreButton} onClick={handleViewMoreClick}>
                                            {t('general.UI.viewMore')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                        {((isMobile || !isMobile)) && (
                            <div className={styles.applicationDetailContainer} >
                                {applicationDetail ? <ApplicationsDetail isMobile={isMobile} applicationDetail={applicationDetail}
                                    setApplicationDetail={setApplicationDetail} />
                                    :
                                    <div className={styles.noDataContainer}>
                                        <Typography title={t('employerStats.noData')} color={'#093559'} fontSize={26} weight={600} />
                                    </div>
                                }
                            </div>
                        )}
                    </>
                )}
            </div>

            <GlobalModal
                show={showUpgradeModal}
                onClose={() => setShowUpgradeModal(false)}
                title={t('general.subscription.upgrade')}
                message={t('general.subscription.upgradeMessage')}
                primaryButtonText={t('general.subscription.upgradeNow')}
                secondaryButtonText={t('general.UI.cancel', { defaultValue: 'Cancel' })}
                navigateTo="/subscriptions"
                secondaryButtonAction={() => setShowUpgradeModal(false)}
            />
        </div>
    );
}

export default EmployerPortalApplication;